import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { apiForm } from '../api/client';
import FileDropZone from '../components/FileDropZone';

// #15 — minimal UNAUTHED upload page served at /u/:token.
//
// The external party drops one file; we POST it to the same path
// (absolute, NOT under /api). The link is single-use: a successful
// upload burns it, and a `valid:false` status (used/expired) shows a
// terminal message. No auth, no nav chrome.
type State = 'checking' | 'ready' | 'invalid' | 'uploading' | 'done' | 'error';

export default function UploadPage() {
  const { token = '' } = useParams();
  const [state, setState] = useState<State>('checking');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    fetch(`/api/upload-links/${encodeURIComponent(token)}/status`)
      .then((r) => r.json())
      .then((j: { valid: boolean }) => {
        if (alive) setState(j.valid ? 'ready' : 'invalid');
      })
      .catch(() => {
        if (alive) setState('invalid');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError('');
    setState('uploading');
    try {
      const form = new FormData();
      form.append('file', file);
      await apiForm<{ ok: boolean }>(`/u/${encodeURIComponent(token)}`, form, {
        absolute: true,
      });
      setState('done');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'upload failed';
      if (/40[49]/.test(msg)) {
        setError('this link is no longer valid (already used or expired).');
        setState('invalid');
      } else if (/413/.test(msg)) {
        setError('file too large.');
        setState('error');
      } else if (/507/.test(msg)) {
        setError('the server is at capacity — please try again later.');
        setState('error');
      } else {
        setError(msg);
        setState('error');
      }
    }
  }

  return (
    <div className="center-page">
      <div className="card">
        <h1>upload a file</h1>
        {state === 'checking' && <p className="muted">checking link…</p>}

        {state === 'invalid' && (
          <>
            <div className="error-banner">
              {error || 'this upload link is invalid, already used, or expired.'}
            </div>
            <p className="muted">Ask the person who sent it for a fresh link.</p>
          </>
        )}

        {state === 'done' && (
          <div className="success-banner">
            Thanks — your file was uploaded. This link is now used up; you can close this
            page.
          </div>
        )}

        {(state === 'ready' || state === 'uploading' || state === 'error') && (
          <>
            <p className="muted" style={{ marginBottom: 16 }}>
              You can upload <strong>one file, once</strong>. It is held in memory and
              handed to the recipient a single time, then wiped. This link works only once.
            </p>
            {error && <div className="error-banner">{error}</div>}
            <form onSubmit={submit}>
              <FileDropZone
                file={file}
                onPick={setFile}
                disabled={state === 'uploading'}
              />
              <div className="actions">
                <button type="submit" disabled={state === 'uploading' || !file}>
                  {state === 'uploading' ? 'uploading…' : 'upload file'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
